/**
 * 신발장과 댓돌 — 현관 신발장.
 */
import { bevelBox, lathe, type MeshWriter } from './kernel';
import { type Piece } from './pieces';

// 이 파일 전용 시드 난수 — 다른 초치성 파일들과 동일한 방식(공용 유틸이 없어 각 파일이 자기 것을 둔다).
function rnd(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── 3. 신발장과 댓돌 ────────────────────────────────────────────────────
// 칸은 여섯인데 이 집엔 다섯이 산다 — 그 여섯째 자리에 놓일 신발(sinbaljang_sin, shrineStory.ts)의
// 치수(len 0.26, w 0.095)가 칸 안에 여유 있게 들어가도록 칸 폭·높이를 역산한다.
const sinbaljang: Piece = {
  id: 'sinbaljang',
  name: '신발장과 댓돌',
  collection: 'sahol',
  category: '대문간',
  clusters: 2, // 신발장 몸체 + 댓돌은 마루 앞뒤로 떨어져 놓이는 별개 덩어리
  note: '칸 6개 · 거주자 5명 — 한 자리가 안 맞는다. sinbaljang_sin(신발 6켤레)이 이 위에 얹히므로 칸 폭(interiorW/3)과 로우 높이(rowH)가 그 신발 치수보다 넉넉해야 한다.',
  dims: { w: 1.3, d: 0.36, h: 0.62, daetdolW: 1.1, daetdolD: 0.42, daetdolH: 0.14 },
  /** 가운데 칸의 신발 한 짝을 앞으로 꺼낸다 (2026-09-16) */
  openable: [{ part: '신발 한 짝', slide: [0, 0.01, -0.16] }],
  build(w: MeshWriter, d) {
    const plinthH = d.h * 0.1;
    const capH = d.h * 0.06;
    const bodyH = d.h - plinthH - capH;
    const rowH = bodyH / 2;
    const sideT = 0.02;
    const dividerT = 0.018;
    const backT = 0.015;
    const interiorW = d.w - sideT * 2;

    w.part('밑받침');
    const plinth = bevelBox(d.w * 0.94, plinthH, d.d * 0.92, 0.006);
    plinth.translate(0, plinthH / 2, 0);
    w.add(plinth, 'oak', 0.4);

    w.part('몸체 옆판');
    for (const sx of [-1, 1]) {
      const side = bevelBox(sideT, bodyH, d.d, 0.005);
      side.translate(sx * (d.w / 2 - sideT / 2), plinthH + bodyH / 2, 0);
      w.add(side, 'oak', 0.4);
    }

    w.part('몸체 뒤판');
    const back = bevelBox(interiorW, bodyH, backT, 0.004);
    back.translate(0, plinthH + bodyH / 2, d.d / 2 - backT / 2);
    w.add(back, 'oak', 0.4);

    // 칸 바닥 3장(맨아래·가운데 선반·맨위) — 이게 있어야 두 층 세 칸으로 실제 나뉘어 보인다.
    w.part('칸 선반');
    for (const ry of [0, rowH, bodyH]) {
      const shelf = bevelBox(interiorW, dividerT, d.d - backT, 0.004);
      shelf.translate(0, plinthH + Math.min(Math.max(ry, dividerT / 2), bodyH - dividerT / 2), -backT / 2);
      w.add(shelf, 'oak', 0.5);
    }

    // 세로 칸막이 2개 — 3칸으로 균등 분할. 남는 폭은 옆판 안쪽에 흡수시켜 신발 폭(0.095)보다
    // 훨씬 넉넉한 칸을 만든다.
    w.part('세로 칸막이');
    const colW = (interiorW - dividerT * 2) / 3;
    let cx = -interiorW / 2 + colW;
    for (let c = 0; c < 2; c++) {
      const div = bevelBox(dividerT, bodyH - dividerT * 2, d.d - backT, 0.004);
      div.translate(cx + dividerT / 2, plinthH + bodyH / 2, -backT / 2);
      w.add(div, 'oak', 0.5);
      cx += dividerT + colW;
    }

    w.part('상판');
    const cap = bevelBox(d.w, capH, d.d, 0.008, 2);
    cap.translate(0, plinthH + bodyH + capH / 2, 0);
    w.add(cap, 'oak', 0.5);

    /* **신발 한 짝** — 가운데 칸 아랫줄에 코를 바깥으로 두고 넣은 가죽 신발.
     * E 로 앞으로 꺼내 본다 (2026-09-16) */
    w.movable('신발 한 짝', () => {
      const shoeW = 0.095;
      const shoeLen = 0.26;
      const body = lathe(
        [
          [shoeW * 0.42, 0],
          [shoeW * 0.5, 0.035],
          [shoeW * 0.46, 0.07],
          [shoeW * 0.16, 0.075],
        ],
        10,
      );
      body.scale(1, 1, 2.0);
      body.translate(0, 0.012, -shoeLen * 0.1);
      body.translate(0, plinthH + dividerT, -0.04);
      w.add(body, 'leather', 0.16);
      const soleG = bevelBox(shoeW * 1.02, 0.016, shoeLen, 0.006, 2);
      soleG.translate(0, plinthH + dividerT + 0.008, -0.04);
      w.add(soleG, 'ebony', 0.2);
    });

    // 댓돌 — 마루 앞, 신발장과는 걸음 한 칸 떨어져 놓인 낮은 디딤돌. 판석 세 장을 이어붙인 모양.
    w.part('댓돌');
    const r2 = rnd(2201);
    const stoneCount = 3;
    const baseSW = d.daetdolW / stoneCount;
    const daetdolZ = -(d.d / 2 + 0.2 + d.daetdolD / 2);
    for (let i = 0; i < stoneCount; i++) {
      const sw = baseSW - 0.008 + (r2() - 0.5) * 0.02;
      const sh = d.daetdolH * (0.85 + r2() * 0.15);
      const sx = -d.daetdolW / 2 + baseSW * (i + 0.5);
      const stone = bevelBox(sw, sh, d.daetdolD, 0.012);
      stone.translate(sx, sh / 2, daetdolZ);
      w.add(stone, 'stone', 0.4);
    }
  },
};

export const SHOERACK_PIECES: Piece[] = [ sinbaljang,];
